import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { templates, type TeamTemplate } from '../lib/api';
import { useEscapeKey } from '../hooks/useEscapeKey';

interface Props {
  onSubmit: (data: { name: string; description?: string; forkFrom?: string }) => void;
  onClose: () => void;
}

export function CreateTemplateModal({ onSubmit, onClose }: Props) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [startFrom, setStartFrom] = useState('blank');

  const { data } = useQuery({
    queryKey: ['templates'],
    queryFn: templates.list,
  });

  const templateList: TeamTemplate[] = data?.data || [];

  useEscapeKey(onClose);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      forkFrom: startFrom !== 'blank' ? startFrom : undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="relative z-10 w-full max-w-md rounded-xl border border-gray-800 bg-surface-850 p-6 shadow-2xl"
      >
        <h2 className="mb-4 text-lg font-semibold">Create Template</h2>

        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
              Template Name
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-lg border border-gray-700 bg-surface-900 px-3 py-2 text-sm text-gray-100 outline-none focus:border-primary-500"
              placeholder="My AI Team"
              autoFocus
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full resize-none rounded-lg border border-gray-700 bg-surface-900 px-3 py-2 text-sm text-gray-100 outline-none focus:border-primary-500"
              placeholder="What does this team do?"
            />
          </div>

          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-gray-500">
              Start From
            </label>
            <select
              value={startFrom}
              onChange={(e) => setStartFrom(e.target.value)}
              className="w-full rounded-lg border border-gray-700 bg-surface-900 px-3 py-2 text-sm text-gray-100 outline-none focus:border-primary-500"
            >
              <option value="blank">Blank Template</option>
              {templateList.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} (v{t.version})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2 text-sm font-medium text-gray-400 hover:bg-gray-800 hover:text-gray-200"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!name.trim()}
            className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-500 disabled:opacity-40"
          >
            Create Template
          </button>
        </div>
      </form>
    </div>
  );
}
